var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Box, Button, CountryFlag, FlexBox, IconButton, Switch, Table, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { produce } from 'immer';
import { getCountryCodeFromPhoneNumber } from 'lib-shared-phone-numbers';
import { useMemo, useRef } from 'react';
import { useDebounce } from 'react-use';
import { numbersApi } from 'apis/numbers';
import { appActions } from 'app/app.actions';
import { getCountryNameFromCountryCode } from 'utils/countries';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { smsFunctionalityStateText } from '../utils/smsFunctionality';
import styles from './smsTableRow.module.scss';
/** Represents a row. */
export const SmsTableRow = ({ smsTableDataItem, onSmsTableDataItemChanged, }) => {
    const dispatch = useAppDispatch();
    const previousSmsFunctionality = useRef(smsTableDataItem.phoneNumber.sms_functionality);
    const { countryCode, countryName } = useMemo(() => {
        const countryCode = getCountryCodeFromPhoneNumber(smsTableDataItem.phoneNumber.e164_format);
        return {
            countryCode,
            countryName: countryCode && getCountryNameFromCountryCode(countryCode),
        };
    }, [smsTableDataItem.phoneNumber]);
    // Debounces the network request when the button gets pressed in quick
    // succession.
    useDebounce(() => {
        const { e164_format, sms_functionality } = smsTableDataItem.phoneNumber;
        if (sms_functionality !== previousSmsFunctionality.current) {
            dispatch(numbersApi.endpoints.updateNumber.initiate({
                phoneNumber: e164_format,
                data: { sms_functionality },
            }))
                .unwrap()
                .then(() => {
                previousSmsFunctionality.current = sms_functionality;
            })
                .catch(() => {
                // Undo the table change as there was an error. This will
                // effectively flip the switch back to its previous value.
                onSmsTableDataItemChanged(produce(smsTableDataItem, (draft) => {
                    draft.phoneNumber.sms_functionality =
                        previousSmsFunctionality.current;
                }));
            });
        }
    }, 500, [smsTableDataItem.phoneNumber.sms_functionality]);
    /** Handles changing the queue of an sms phone number. */
    const handleSmsQueueChange = () => {
        dispatch(appActions.showModal({
            modal: { type: 'settings:editSmsQueue', arg: { smsTableDataItem } },
        }));
    };
    /** Handles changing the sms functionality of an sms phone number. */
    const handleSmsFunctionalityChange = () => {
        onSmsTableDataItemChanged(produce(smsTableDataItem, (draft) => {
            draft.phoneNumber.sms_functionality =
                !smsTableDataItem.phoneNumber.sms_functionality;
        }));
    };
    /**
     * Handles clicking the request for change of sms functionality for manual
     * carriers. (DIDWW)
     */
    const handleSmsFunctionalityStateChange = () => __awaiter(void 0, void 0, void 0, function* () {
        const { updatedSmsTableDataItem } = yield dispatch(appActions.showModal({
            modal: {
                type: 'settings:changeSmsFunctionality',
                arg: { smsTableDataItem },
            },
        }));
        onSmsTableDataItemChanged(updatedSmsTableDataItem);
    });
    const { phoneNumber: { carrier, e164_format, name, sms_functionality, sms_functionality_state, }, queue, } = smsTableDataItem;
    return (_jsxs(Table.Tr, { children: [_jsx(Table.Td, { children: _jsxs(FlexBox, Object.assign({ gap: sizes.spacer['300'] }, { children: [countryCode && (_jsx(Box, Object.assign({ className: styles.countryFlagContainer }, { children: _jsx(CountryFlag, { countryCode: countryCode, size: 20 }) }))), _jsxs(FlexBox, Object.assign({ flexDirection: "column" }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "2" }, { children: e164_format })), countryName && (_jsx(Text.Paragraph, Object.assign({ variant: "4", subdued: true }, { children: countryName })))] }))] })) }), _jsx(Table.Td, { children: name }), _jsx(Table.Td, { children: queue ? (_jsxs(FlexBox, Object.assign({ gap: "250", alignItems: "center" }, { children: [_jsx(Text.Paragraph, { children: queue.name }), queue.is_archived && (_jsx(Badge, Object.assign({ variant: "error", size: "small" }, { children: "Archived" })))] }))) : (_jsx(Text.Paragraph, Object.assign({ as: "i" }, { children: "No queue" }))) }), _jsx(Table.Td, { children: _jsxs(FlexBox, Object.assign({ alignItems: "center", justifyContent: "space-between", gap: sizes.spacer['300'] }, { children: [_jsx(Switch, { name: name !== null && name !== void 0 ? name : '', value: name !== null && name !== void 0 ? name : '', checked: sms_functionality, onChange: handleSmsFunctionalityChange, disabled: carrier === 'didww' }), carrier === 'didww' && sms_functionality_state && (_jsx(Button, Object.assign({ className: styles.button, variant: "secondary", onClick: handleSmsFunctionalityStateChange, disabled: ['pending_activation', 'pending_deactivation'].includes(sms_functionality_state) }, { children: smsFunctionalityStateText[sms_functionality_state] }))), _jsx(Tooltip, Object.assign({ content: "Edit", dark: true }, { children: _jsx(IconButton, { variant: "tertiary", icon: "pen", onClick: handleSmsQueueChange }) }))] })) })] }));
};
